import { useEffect, useState, type ReactNode } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Archive, FileStack, NotebookPen, Sparkles } from "lucide-react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { NotesPanelArchive } from "@/components/notes/NotesPanelArchive";

type VaultSubTab = "templates" | "prompts" | "archive";
type ArchiveSection = "published" | "notes";

const SUB_TAB_KEY = (ws: string | undefined) =>
  `composer.shell.vault.tab.${ws ?? "anon"}`;
const ARCHIVE_SECTION_KEY = (ws: string | undefined) =>
  `composer.shell.vault.archive-section.${ws ?? "anon"}`;

export type VaultPanels = {
  templates?: ReactNode;
  prompts?: ReactNode;
  archive?: ReactNode;
};

type Props = {
  workspaceId?: string;
  panels?: VaultPanels;
};

const ComingSoon = ({ title, description }: { title: string; description: string }) => (
  <div className="flex h-full items-center justify-center p-6">
    <div className="max-w-md rounded-md border border-dashed border-border/60 bg-muted/30 p-4 text-center">
      <p className="text-sm font-medium">{title}</p>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      <p className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">Coming next</p>
    </div>
  </div>
);

const ArchiveTabContent = ({
  workspaceId,
  publishedPanel,
}: {
  workspaceId?: string;
  publishedPanel?: ReactNode;
}) => {
  const { active } = useWorkspace();
  const activeWsId = workspaceId ?? active?.id;
  const [section, setSection] = useState<ArchiveSection>(() => {
    if (typeof window === "undefined") return "published";
    const stored = window.localStorage.getItem(ARCHIVE_SECTION_KEY(activeWsId));
    return stored === "notes" ? "notes" : "published";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(ARCHIVE_SECTION_KEY(activeWsId), section);
  }, [section, activeWsId]);

  return (
    <Tabs value={section} onValueChange={(v) => setSection(v as ArchiveSection)} className="flex h-full flex-col">
      <div className="shrink-0 border-b border-border/60 px-2 py-1.5">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="published" className="gap-1.5">
            <Archive className="h-3.5 w-3.5" />
            <span>Published</span>
          </TabsTrigger>
          <TabsTrigger value="notes" className="gap-1.5">
            <NotebookPen className="h-3.5 w-3.5" />
            <span>Notes</span>
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="published" className="mt-0 min-h-0 flex-1 overflow-auto">
        {publishedPanel}
      </TabsContent>
      <TabsContent value="notes" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
        {activeWsId ? (
          <NotesPanelArchive workspaceId={activeWsId} />
        ) : (
          <ComingSoon title="Notes" description="Select a workspace to start capturing notes." />
        )}
      </TabsContent>
    </Tabs>
  );
};

export const VaultTabPanel = ({ workspaceId, panels }: Props) => {
  const [sub, setSub] = useState<VaultSubTab>(() => {
    if (typeof window === "undefined") return "templates";
    const stored = window.localStorage.getItem(SUB_TAB_KEY(workspaceId));
    if (stored === "templates" || stored === "prompts" || stored === "archive") return stored;
    return "templates";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SUB_TAB_KEY(workspaceId), sub);
  }, [sub, workspaceId]);

  return (
    <Tabs
      value={sub}
      onValueChange={(v) => setSub(v as VaultSubTab)}
      className="flex h-full flex-col"
    >
      <div className="shrink-0 border-b border-border/60 p-2">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="templates" className="gap-1.5">
            <FileStack className="h-3.5 w-3.5" />
            <span>Templates</span>
          </TabsTrigger>
          <TabsTrigger value="prompts" className="gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Prompts</span>
          </TabsTrigger>
          <TabsTrigger value="archive" className="gap-1.5">
            <Archive className="h-3.5 w-3.5" />
            <span>Archive</span>
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="templates" className="mt-0 min-h-0 flex-1 overflow-auto">
        {panels?.templates}
      </TabsContent>
      <TabsContent value="prompts" className="mt-0 min-h-0 flex-1 overflow-auto">
        {panels?.prompts ?? (
          <ComingSoon
            title="Prompts"
            description="Excerptus Prompts and your own prompt library will live here."
          />
        )}
      </TabsContent>
      <TabsContent value="archive" className="mt-0 min-h-0 flex-1 overflow-auto">
        <ArchiveTabContent workspaceId={workspaceId} publishedPanel={panels?.archive} />
      </TabsContent>
    </Tabs>
  );
};

export default VaultTabPanel;
